import { getCurrentUser } from "@/global";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { buildCardDocument } from "./cardSdk";
import { bindCardPort } from "./cardBridge";
import type { CardDataKind, CardDataOptions } from "./cardDataKinds";
import { executeCardState } from "./cardStateApi";

/** Trusted host context. HTML and metadata originate in the portal's selected card/session. */
export interface HtmlCardHostProps {
  html: string;
  title: string;
  modelId?: string;
  investigationId: string;
  cardId: string;
  sessionId?: string;
  sessionVersion?: number | null;
  saved: boolean;
  /** Data bridge v1 message, pushed to the card on load and whenever it changes. Omit for v2 cards. */
  decisionPayload?: unknown;
  /** Data bridge v2: resolves `decisively.data.get` requests. */
  getData?: (kind: CardDataKind, options: CardDataOptions) => Promise<unknown>;
}
/** Mount a fresh document whenever context or HTML changes; the stored state identity stays stable. */
export function HtmlCardHost(props: HtmlCardHostProps): React.JSX.Element {
  const [restart, setRestart] = useState(0);
  const key = JSON.stringify([
    props.modelId,
    props.investigationId,
    props.cardId,
    props.sessionId,
    props.sessionVersion,
    props.html,
    props.saved,
    restart,
  ]);
  if (!props.html) return <span className="text-sm text-muted-foreground">No file uploaded yet.</span>;
  return (
    <BoundHtmlCard
      key={key}
      {...props}
      restart={() => setRestart((value) => value + 1)}
    />
  );
}
function BoundHtmlCard(props: HtmlCardHostProps & { restart: () => void }): React.JSX.Element {
  const iframe = useRef<HTMLIFrameElement>(null);
  const [navigated, setNavigated] = useState(false);
  const instance = useMemo(() => crypto.randomUUID(), []);
  const document = useMemo(
    () => buildCardDocument(props.html, instance, window.location.origin),
    [props.html, instance],
  );
  const loaded = useRef(false);
  const sendLegacy = useRef<() => void>(() => {});
  const payload = useRef(props.decisionPayload);
  payload.current = props.decisionPayload;
  // Read through a ref so a new data source never rebinds (and so closes) the card's port.
  const getData = useRef(props.getData);
  getData.current = props.getData;
  const dispose = useRef<() => void>(() => {});

  useLayoutEffect(() => {
    const frame = iframe.current?.contentWindow;
    if (!frame) return;
    const identity = (): string =>
      JSON.stringify([getCurrentUser()?.profile.sub, localStorage.getItem("decisively.tenant")]);
    // Token refreshes do not change ownership; user/tenant switches do.
    const boundIdentity = identity();
    let active = true;
    let connected = false;
    let closePort = (): void => {};
    const digest = crypto.subtle
      .digest("SHA-256", new TextEncoder().encode(props.html))
      .then((bytes) => Array.from(new Uint8Array(bytes), (byte) => byte.toString(16).padStart(2, "0")).join(""));
    sendLegacy.current = (): void => {
      if (active && loaded.current && payload.current !== undefined) frame.postMessage(payload.current, "*");
    };
    const onMessage = (event: MessageEvent): void => {
      if (!active || event.source !== frame || event.origin !== "null") return;
      const data: unknown = event.data;
      if (typeof data !== "object" || !data) return;
      if ("type" in data && data.type === "decisively:ready") {
        sendLegacy.current();
        return;
      }
      if (
        connected ||
        !("type" in data) ||
        data.type !== "decisively:state:hello" ||
        !("version" in data) ||
        data.version !== 1 ||
        !("instance" in data) ||
        data.instance !== instance ||
        Object.keys(data).length !== 3 ||
        event.ports.length !== 1
      )
        return;
      connected = true;
      closePort = bindCardPort(event.ports[0], {
        sessionVersion: props.sessionVersion ?? null,
        async execute(scope, request) {
          if (!active || identity() !== boundIdentity || !props.saved)
            return { ok: false, error: { code: "NOT_AVAILABLE" } };
          const id = scope === "project" ? props.modelId : props.sessionId;
          if (!id) return { ok: false, error: { code: "NOT_AVAILABLE" } };
          const htmlHash = await digest;
          if (!active || identity() !== boundIdentity) return { ok: false, error: { code: "CONTEXT_CHANGED" } };
          const response = await executeCardState(scope, id, props.investigationId, props.cardId, {
            ...request,
            htmlHash,
          });
          if (!active || identity() !== boundIdentity) return { ok: false, error: { code: "CONTEXT_CHANGED" } };
          return response;
        },
        async getData(kind, options) {
          const changed = () => Object.assign(new Error("CONTEXT_CHANGED"), { code: "CONTEXT_CHANGED" });
          if (!active || identity() !== boundIdentity) throw changed();
          if (!getData.current) throw Object.assign(new Error("NOT_AVAILABLE"), { code: "NOT_AVAILABLE" });
          const result = await getData.current(kind, options);
          if (!active || identity() !== boundIdentity) throw changed();
          return result;
        },
      });
    };
    const stop = (): void => {
      active = false;
      closePort();
      window.removeEventListener("message", onMessage);
    };
    dispose.current = stop;
    window.addEventListener("message", onMessage);
    return stop;
  }, [
    instance,
    props.html,
    props.saved,
    props.modelId,
    props.sessionId,
    props.investigationId,
    props.cardId,
    props.sessionVersion,
  ]);

  useLayoutEffect(() => {
    sendLegacy.current();
  }, [props.decisionPayload]);
  return (
    <div className="relative h-full w-full">
      <iframe
        ref={iframe}
        title={props.title}
        srcDoc={document}
        sandbox="allow-scripts allow-downloads"
        className="h-full w-full border-0"
        onLoad={() => {
          if (loaded.current) {
            dispose.current();
            setNavigated(true);
            return;
          }
          loaded.current = true;
          sendLegacy.current();
        }}
      />
      {navigated ? (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-background p-4"
          role="alert"
        >
          <p>The card navigated away. Reopen it to reconnect safely.</p>
          <button
            type="button"
            className="rounded border px-3 py-2"
            onClick={props.restart}
          >
            Reopen card
          </button>
        </div>
      ) : null}
    </div>
  );
}
